var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
const allowedMuteTypes = ['audio'];
const allowedVolumeTypes = ['audio'];
const allowedTypes = {
    amplifier: allowedVolumeTypes,
    assistiveListeningMute: allowedMuteTypes,
    assistiveListeningMuteStatus: allowedMuteTypes,
    assistiveListeningVolume: allowedVolumeTypes,
    audioInputMute: allowedMuteTypes,
    audioInputMuteStatus: allowedMuteTypes,
    audioInputVolume: allowedVolumeTypes,
    mics: allowedMuteTypes,
    micsMuteStatus: allowedMuteTypes,
    overflowRoomMute: allowedMuteTypes,
    overflowRoomMuteStatus: allowedMuteTypes,
    overflowRoomVolume: allowedVolumeTypes,
    preset: [],
    receiveGain: [],
    soundReinforcementMute: allowedMuteTypes,
    soundReinforcementMuteStatus: allowedMuteTypes,
    soundReinforcementVolume: allowedVolumeTypes,
    telephony: [],
};
function isInogeniAudioAudioChannelConfig(config) {
    return typeof config === 'string';
}
class Channel {
    constructor(socket, key, name, log) {
        this.socket = socket;
        this.key = key;
        this.name = name;
        this.log = log;
        this.isMuteConfigured = false;
        this.isVolumeConfigured = false;
        this.lastVolume = 0; // last volume to support 'muting' and 'unmuting'
        this.type = 'audio';
        this.socket = socket;
    }
    getFader(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`fader not supported for ${this.key}`);
        });
    }
    getMute(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`mute not supported for ${this.key}`);
        });
    }
    setFader(value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`fader not supported for ${this.key}`);
        });
    }
    setMute(value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`mute not supported for ${this.key}`);
        });
    }
    isAllowed(filter) {
        return allowedTypes[filter].includes(this.type);
    }
    matches(key) {
        return this.key === key;
    }
    makeCommand(cmd) {
        const cmdWrap = this.socket.port === 50000 ? '$' : '<';
        return `${cmdWrap}${cmd}${cmdWrap}`;
    }
}
class AudioChannel extends Channel {
    constructor(socket, key, name, log) {
        super(socket, key, name, log);
        this.socket = socket;
        this.key = key;
        this.name = name;
        this.log = log;
    }
    setFader(value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const [inOrOut, chan] = this.key.split('.');
            const value2sComplement = value < 0 ? value + 256 : value;
            const cmd = `A${inOrOut}${chan}_${value2sComplement}`;
            yield this.socket.send(this.makeCommand(cmd), /ACK/i, tags);
            return value;
        });
    }
    setMute(value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const [inOrOut, chan] = this.key.split('.');
            if (value) {
                const value2sComplement = 166; // indicates muting the channel
                const cmd = `A${inOrOut}${chan}_${value2sComplement}`;
                yield this.socket.send(this.makeCommand(cmd), /ACK/i, tags);
            }
            return value;
        });
    }
    matches(key) {
        return this.key === key;
    }
}
class Channels {
    constructor(log) {
        this.log = log;
        this.channels = [];
    }
    addChannel(chan) {
        this.channels.push(chan);
    }
    get areAnyConfigured() {
        for (const channel of this.channels) {
            if (channel.isMuteConfigured || channel.isVolumeConfigured)
                return true;
        }
        return false;
    }
    clearChannels() {
        this.channels.length = 0;
    }
    computeFilters(type) {
        return {
            amplifier: allowedTypes.amplifier.includes(type),
            assistiveListeningMute: allowedTypes.assistiveListeningMute.includes(type),
            assistiveListeningMuteStatus: allowedTypes.assistiveListeningMuteStatus.includes(type),
            assistiveListeningVolume: allowedTypes.assistiveListeningVolume.includes(type),
            audioInputMute: allowedTypes.audioInputMute.includes(type),
            audioInputMuteStatus: allowedTypes.audioInputMuteStatus.includes(type),
            audioInputVolume: allowedTypes.audioInputVolume.includes(type),
            mics: allowedTypes.mics.includes(type),
            micsMuteStatus: allowedTypes.micsMuteStatus.includes(type),
            overflowRoomMute: allowedTypes.overflowRoomMute.includes(type),
            overflowRoomMuteStatus: allowedTypes.overflowRoomMuteStatus.includes(type),
            overflowRoomVolume: allowedTypes.overflowRoomVolume.includes(type),
            preset: false,
            receiveGain: false,
            soundReinforcementMute: allowedTypes.soundReinforcementMute.includes(type),
            soundReinforcementMuteStatus: allowedTypes.soundReinforcementMuteStatus.includes(type),
            soundReinforcementVolume: allowedTypes.soundReinforcementVolume.includes(type),
            telephony: false,
        };
    }
    getAllConfiguredChannels() {
        return this.channels.filter(channel => channel.isMuteConfigured || channel.isVolumeConfigured);
    }
    getByKey(key) {
        return this.channels.find(channel => channel.key === key);
    }
    getChannel(id) {
        for (const channel of this.channels) {
            if (channel.matches(id))
                return channel;
        }
        return undefined;
    }
    getChannelType(id) {
        for (const channel of this.channels) {
            if (channel.matches(id))
                return channel === null || channel === void 0 ? void 0 : channel.type;
        }
        return undefined;
    }
    getConfiguredChannels(control) {
        return this.channels.filter(channel => control === 'mute' ? channel.isMuteConfigured : channel.isVolumeConfigured);
    }
    getDetected() {
        const detectedChannels = [];
        for (const channel of this.channels) {
            const detectedChannel = {
                channel: channel.key,
                type: channel.type,
                name: channel.name,
                filters: this.computeFilters(channel.type),
            };
            if (channel.name)
                detectedChannel.name = channel.name;
            detectedChannels.push(detectedChannel);
        }
        return detectedChannels;
    }
}
export class InogeniShare2u {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.channels.getDetected();
            }),
        };
        this.displayBlank = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand(`POLL`), /BLACK\s*=>\s*(enabled|disabled)/i, tags);
                return match[1].toLowerCase() === 'enabled';
            }),
            set: (blank, tags) => __awaiter(this, void 0, void 0, function* () {
                yield this.socket.send(this.makeCommand(blank ? 'BLK' : 'SHW'), /ACK/i, tags);
                return blank;
            }),
            asyncHandlers: [
                {
                    regExp: /BLACK\s*=>\s*(enabled|disabled)/i,
                    callback: (match) => {
                        this.state.displayBlank.update(match[1].toLowerCase() === 'enabled');
                    },
                },
            ],
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand('GET'), /StreamerApp\s*=>\s*([\d\.]+)/i, tags);
                return match[1];
            }),
        };
        this.mute = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    throw new Error(`no channel for key = ${key}`);
                return this.state.mutes.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _a, _b;
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    throw new Error(`no channel for key = ${key}`);
                // Save current volume value if muting, use previous volume value if unmuting.
                if (value) {
                    channel.lastVolume = (_a = this.state.volumes.values[key]) !== null && _a !== void 0 ? _a : 0;
                    channel.setMute(value, tags);
                    // The Inogeni documentation says to set the volume to 166 to mute the channel. This
                    // corresponds to a value of -90.
                    this.state.volumes.update(key, -90);
                }
                else {
                    // If the channel only has a last recorded volume of 166 (-90) because it was muted,
                    // then restore to 0 when unmuted.
                    const lastRecordedVolume = (_b = channel === null || channel === void 0 ? void 0 : channel.lastVolume) !== null && _b !== void 0 ? _b : -90;
                    const channelLastVolume = lastRecordedVolume > -90 ? lastRecordedVolume : 0;
                    channel.setFader(channelLastVolume, tags);
                    this.state.volumes.update(key, channelLastVolume);
                }
                return value;
            }),
            getKey: this.getKey.bind(this),
            validate: this.validate.bind(this), // happens before constructor binding
        };
        this.videoMatrixRoutes = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand('POLL'), /^((?:S|SS|TB|BS|PP[TB][LR]|SW)[12])/i, tags);
                return match[1];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand(value), /(ACK|NACK)/i, tags);
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), 1000);
                return match[1].toLowerCase() === 'ack'
                    ? value
                    : this.state.videoMatrixRoutes.values[key];
            }),
            asyncHandlers: {
                regExp: /VIEW\s*=>\s*(\S+)/i,
                callback: (match) => {
                    this.state.videoMatrixRoutes.update('1', match[1]);
                },
            },
        };
        this.volume = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    throw new Error(`no channel for key = ${key}`);
                return this.state.volumes.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    throw new Error(`no channel for key = ${key}`);
                return yield channel.setFader(value, tags);
            }),
            getKey: this.getKey.bind(this),
            range: (config, info) => __awaiter(this, void 0, void 0, function* () {
                if (info.volumeType === 'trim')
                    return { max: 6, min: -18, round: 1 };
                return { max: 6, min: -44, round: 1 };
            }),
            asyncHandlers: [
                {
                    regExp: /A(IN|OUT)(\d+)\s*=>\s*(\d+)/i,
                    callback: (match) => {
                        const key = `${match[1].toLowerCase() === 'in' ? 'IN' : 'OUT'}.${match[2]}`;
                        const value = parseInt(match[3], 10);
                        this.state.mutes.update(key, value === 166); // muted if value is 166
                        // Convert from two's complement.
                        this.state.volumes.update(key, value > 6 ? value - 256 : value);
                    },
                },
            ],
            validate: this.validate.bind(this), // happens before constructor binding
        };
        // polling
        this.polls = [
            {
                intervalMs: 8000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    yield this.socket.send(this.makeCommand('GET'), /NETMASK/, ['poll']);
                }),
            },
        ];
        this.channels = new Channels(log);
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            this.channels.clearChannels();
            const inputNames = ['Input USB 1', 'Input USB 2', 'Input HDMI', 'Input Line'];
            const outputNames = ['Output Line', 'Output USB 1', 'Output USB 2', 'Output HDMI'];
            for (let i = 1; i <= 4; i++) {
                this.channels.addChannel(new AudioChannel(this.socket, `IN.${i}`, inputNames[i - 1], this.log));
                this.channels.addChannel(new AudioChannel(this.socket, `OUT.${i}`, outputNames[i - 1], this.log));
            }
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            const initializerSteps = [
                {
                    errorCode: 'verifyFailed',
                    send: this.makeCommand('GET'),
                    next: /StreamerApp/i,
                },
            ];
            return yield CommandSocketInitializer.initialize(this.socket, initializerSteps, credentials);
        });
    }
    // other class methods
    getKey(config) {
        if (isInogeniAudioAudioChannelConfig(config))
            return config;
        throw new Error(`invalid config: ${JSON.stringify(config)}`);
    }
    makeCommand(cmd) {
        const cmdWrap = this.socket.port === 50000 ? '$' : '<';
        return `${cmdWrap}${cmd}${cmdWrap}`;
    }
    validate(config, info) {
        return __awaiter(this, void 0, void 0, function* () {
            const key = this.getKey(config);
            if (isInogeniAudioAudioChannelConfig(config)) {
                if (info.type === 'volume' && info.filter === 'amplifier')
                    this.volumeConfig = config;
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    return 'missing';
                if (info.type === 'volume') {
                    channel.isVolumeConfigured = true;
                    // Check if volume mute can be supported by the channel.
                    if (config === this.volumeConfig && allowedMuteTypes.includes(channel.type)) {
                        channel.isMuteConfigured = true;
                    }
                }
                else if (info.type === 'mute') {
                    if (allowedMuteTypes.includes(channel.type))
                        channel.isMuteConfigured = true;
                }
                return 'ok';
            }
            this.log.error(`invalid config: ${JSON.stringify(config)}`);
            return 'incompatible';
        });
    }
}
// static properties
InogeniShare2u.channelsConfigName = 'eclerVidaControlChannels';
InogeniShare2u.driverType = 'socketDriver';
InogeniShare2u.key = '-OK4AC7xAu7obxulumoZ';
InogeniShare2u.baseSocketOptions = {
    sendTerminator: '\r',
    sendTimeoutMs: 1000,
};
InogeniShare2u.socketOptions = [
    Object.assign(Object.assign({}, InogeniShare2u.baseSocketOptions), { port: 50000 }),
    Object.assign(Object.assign({}, InogeniShare2u.baseSocketOptions), { port: 4999 }),
];
